import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import { COMMIT, MetricULS } from '../utils/constants';
import { ulsHourlyPoints } from '../utils/ulsMetrics';
import { groupProcesses, renderGroupedProcessOptions } from '../utils/processGrouping.tsx';
import { getBasicChartOptions, CommitDetailsGraph, getGraphData, getDatasetFromTimeSeries } from './CommitDetailsGraph';
import { Panel, PanelType } from '@fluentui/react/lib/Panel';
import { Callout, DirectionalHint, TextField } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import { useRef } from 'react';
import { VsoLink, ParentButtonsComponent, LegendButton } from './CommonElements.tsx'
import { useConfig } from "../configContext";
import '../styles/common.css'; // Import CSS styles
import { CommitDetails } from '../interfaces/CommitDetails.ts';
import { Service } from '../interfaces/Service.ts';

function ThresholdRow({ label, description, value, onChange }) {
    return (
        <div style={{ marginBottom: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px' }}>
                <span style={{ fontWeight: 600, fontSize: '13px', flex: 1 }}>{label}</span>
                <TextField
                    type="number"
                    defaultValue={value?.toString() ?? ''}
                    onBlur={(e: any) => {
                        const val = e.target.value === '' ? null : Number(e.target.value);
                        onChange(val);
                    }}
                    onKeyDown={(e: any) => {
                        if (e.key === 'Enter') (e.target as any).blur();
                    }}
                    styles={{
                        root: { width: '90px' },
                        fieldGroup: { height: '28px', minHeight: '28px' }
                    }}
                />
                {value !== null && value !== undefined
                    ? <button
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', fontSize: '15px', color: '#666' }}
                        onClick={() => onChange(null)}
                        title="Clear"
                      >×</button>
                    : <span style={{ width: '19px' }} />
                }
            </div>
            <div style={{ fontSize: '12px', color: '#666', paddingRight: '4px' }}>{description}</div>
        </div>
    );
}

export function GraphView({ commits, auditorView, rowSize, navState, service }) {
    const environmentIds = service?.Environments?.map(env => env.id) ?? [];
    const env = navState.deployedTo;
    // Determine the highest environment ID
    const highestEnvId = Math.max(...environmentIds, 0);
    const maxEnv = env > highestEnvId ? highestEnvId : env;


    // make a copy of the array to avoid randomizing pagination
    const sortedCommits = commits.slice(0);
    sortedCommits.sort((a, b) => {
        const aMax = a.max && a.max[maxEnv] !== undefined ? a.max[maxEnv] : 0;
        const bMax = b.max && b.max[maxEnv] !== undefined ? b.max[maxEnv] : 0;
        return bMax - aMax;
    })
    const rows = [];
    const rowCount = Math.floor((sortedCommits.length + rowSize - 1) / rowSize);
    // todo: sort
    for (var i = 0; i < rowCount; i++) {
        rows.push(sortedCommits.slice(i * rowSize, (i + 1) * rowSize));
    }
    //const row = [];
    //row.push(commits[0]);
    //rows.push(row);

    if (!commits || commits.length === 0) {
        return <div>No commits found for the selected environment.</div>;
    }

    return (
        <div className="GraphView">
            <table>
                <tbody>
                    {rows.map(r => (<GraphRow rowItems={r} rowSize={rowSize} env={env} metric={Number(navState.metric)} process={Number(navState.process)} service={service} navState={navState} />))}
                </tbody>
            </table>
        </div>
    );
}

export function GraphViewButtons({ navState, updateStateAndNavigate, showAdvancedSearch, setShowAdvancedSearch }) {
    const path = navState.path;
    const config = useConfig();
    const segments = path.split('/');
    const serviceName = segments[2];
    console.log("servicename in graphview button", serviceName);
    const service = config.services.find((s: Service) => s.name === serviceName) as Service | undefined;
    console.log("service in graphview", service);
    const serviceEnvironments = service?.Environments ?? [];
    console.log("service envs", serviceEnvironments);
    const environmentIds = serviceEnvironments.map(env => env.id);
    // Determine the highest environment ID
    const highestEnvId = Math.max(...environmentIds, 0);
    const processes = service?.Processes ?? [];
    console.log("processes in graphview", processes);
    const env = Number(navState.deployedTo)
    const metric = Number(navState.metric);
    const process = Number(navState.process);

    const [isThresholdOpen, { toggle: toggleThreshold, setFalse: closeThreshold }] = useBoolean(false);
    const thresholdButtonRef = useRef(null);

    const thresholdLabel = (() => {
        const parts = [];
        if (navState.minThreshold !== null && navState.minThreshold !== undefined)
            parts.push(`> ${navState.minThreshold}`);
        if (navState.minIncrease !== null && navState.minIncrease !== undefined)
            parts.push(`Δ > ${navState.minIncrease}`);
        return parts.length ? parts.join(' | ') : 'Threshold';
    })();


    // Get metrics for the selected process
    const selectedProcess = processes.find(p => p.id === process);
    console.log("selected process in graphview", selectedProcess);
    const availableMetrics = selectedProcess ? Object.values(selectedProcess.metrics) : [];
    console.log("available metrics in graphview", availableMetrics);

    const envOptions = [
        {
            key: highestEnvId + 1, // Use a unique key for the "All Environments" option
            text: "All Environments",
            selected: (env === (highestEnvId + 1)) // Check if the current state matches this option
        },
        ...serviceEnvironments.map(e => ({
            key: e.id,
            text: e.name,
            selected: (env === e.id)
        })),
    ];

    const groupedProcesses = groupProcesses(processes);


    const metricOptions = Object.values(availableMetrics).map(m => ({
        key: m.id,
        text: m.name,
        selected: (metric === m.id)
    }));


    //{(event, option) => { debugger; updateStateAndNavigate((s) => s.metric = option.key.toString()) }}
    return (
        <>
            <div className="search-set">
                <select className='filter-dropdown graphview-filter-first'
                    onChange={(event) => { updateStateAndNavigate((s) => s.deployedTo = Number(event.target.value), true) }}>
                    {envOptions.map(option => (
                        <option key={option.key} value={option.key} selected={option.key === env}>{option.text}</option>
                    ))}
                </select>
                <select className='filter-dropdown'
                    value={process}
                    onChange={(event) => { updateStateAndNavigate((s) => s.process = event.target.value.toString(), true) }}>
                    {renderGroupedProcessOptions(groupedProcesses)}
                </select>
                <select className='filter-dropdown'
                    onChange={(event) => { updateStateAndNavigate((s) => s.metric = event.target.value.toString(), true) }}>
                    {metricOptions.map(option => (
                        <option key={option.key} value={option.key} selected={option.key === metric}>{option.text}</option>
                    ))}
                </select>
                <div ref={thresholdButtonRef} style={{ display: 'inline-block' }}>
                    <button className="topbutton" onClick={toggleThreshold}>{thresholdLabel}</button>
                    {isThresholdOpen && (
                        <Callout
                            target={thresholdButtonRef.current}
                            onDismiss={closeThreshold}
                            directionalHint={DirectionalHint.bottomLeftEdge}
                            setInitialFocus
                        >
                            <div style={{ padding: '16px', minWidth: '340px' }}>
                                <div style={{ fontSize: '12px', color: '#888', marginBottom: '12px' }}>
                                    Setting a threshold clears advanced search filters.
                                </div>
                                <ThresholdRow
                                    label="Peak value exceeds"
                                    description="Only show commits whose highest sampled metric value is greater than this. Use 0 to exclude commits with no data."
                                    value={navState.minThreshold}
                                    onChange={(v) => updateStateAndNavigate((s) => {
                                        s.minThreshold = v;
                                        if (v !== null) {
                                            s.asUser = null; s.commitId = null;
                                            s.buildNumber = null; s.controlId = null;
                                            s.startDate = null; s.endDate = null; s.farmType = null;
                                        }
                                    }, true)}
                                />
                                <ThresholdRow
                                    label="Change exceeds"
                                    description="Only show commits where the metric changed by more than this amount (peak minus baseline, regardless of direction). Useful for isolating commits with significant absolute impact."
                                    value={navState.minIncrease}
                                    onChange={(v) => updateStateAndNavigate((s) => {
                                        s.minIncrease = v;
                                        if (v !== null) {
                                            s.asUser = null; s.commitId = null;
                                            s.buildNumber = null; s.controlId = null;
                                            s.startDate = null; s.endDate = null; s.farmType = null;
                                        }
                                    }, true)}
                                />
                            </div>
                        </Callout>
                    )}
                </div>
                {setShowAdvancedSearch && (
                    <button
                        className="topbutton advanced-search-button"
                        onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}>
                        {showAdvancedSearch ? 'Hide' : 'Advanced Search'}
                    </button>
                )}
            </div>
        </>
    )
}


function GraphRow({ rowItems, rowSize, env, metric, process, service, navState }) {
    return (
        <tr>
            {rowItems.map(i => (<CommitSmallGraph commit={i} env={env} process={process} metric={metric} service={service} navState={navState} />))}
        </tr>
    )
}

function CommitSmallGraph({ commit, env, metric, process, service, navState }) {
    const [isOpen, { setTrue: openPanel, setFalse: dismissPanel }] = useBoolean(false);
    const environmentIds = service?.Environments?.map(environment => environment.id) ?? [];
    const highestEnvId = Math.max(...environmentIds, 0);
    const envs = [];
    if (env <= highestEnvId) {
        envs.push(env);
    }
    else {
        envs.push(...environmentIds);
    }

    const isRegression = commit.regressions.some(r => r.source === 2 && r.metricType === metric && r.processId === process && r.isRegression && (env > highestEnvId || r.environmentId === env));

    const datasets = [];
    for (const targetEnv of envs) {
        const details = getGraphData(commit, targetEnv, process, COMMIT, metric, true);
        datasets.push(metric === MetricULS
            ? { data: ulsHourlyPoints(details), spanGaps: false, fill: false }
            : getDatasetFromTimeSeries(null, details, commit, targetEnv, false, null, null, false));
    }
    const options: any = getBasicChartOptions(metric, COMMIT, true);
    options.scales.x.title.text = '';
    options.scales.x.ticks = { display: false };
    options.scales.y.title.text = '';
    options.scales.y.ticks = {
        font: {
            size: 12,
            weight: 'bold'
        }
    };
    options.plugins.legend = { display: false };
    options.elements = {
        point: { pointStyle: false }
    }
    options.animation = false; // looks kind of cool when they're on, but too busy
    for (const d of datasets) d.label = metric === MetricULS ? 'Tracked-event occurrences' : '';
    const data: any = {
        datasets: datasets,
    };

    options.onClick = (event, elements, chart) => {
        openPanel();
    }

    options.responsive = false;

    let style = {};
    let className = '';
    if (isRegression && !navState.regressionsOnly) {
        style = { backgroundColor: 'rgba(220, 38, 38, 0.1)' };
        className = 'regression-cell';
    }

    return (
        <td style={style} className={className}>
            <Line data={data} options={options} width={600} height={400} title={commit.commitMetaData.title} />
            <div>
                <Panel
                    isLightDismiss
                    isOpen={isOpen}
                    type={PanelType.medium}
                    onDismiss={dismissPanel}
                    closeButtonAriaLabel="Close"
                    headerText={commit.commitMetaData.title}
                >
                    <CommitDetailPanelContent commit={commit}
                        service={service}
                        env={env}
                        process={process}
                        metric={metric} />
                </Panel>
            </div>
        </td>
    )
}

export function CommitDetailPanelContent(props: { service: Service; commit: CommitDetails; process: number; metric: number; env: number; }) {
    const { service, commit, process, metric, env } = props;

    const environmentIds = service?.Environments?.map(environment => environment.id) ?? [];
    const highestEnvId = Math.max(...environmentIds, 0);

    // For "Full Details" link, default to highest env if "All Environments" is selected
    const detailsEnv = env > highestEnvId ? highestEnvId : env;

    // For graph display, determine which environments to show
    const envsToDisplay: number[] = [];
    if (env > highestEnvId) {
        // "All Environments" selected - show all environment lines
        envsToDisplay.push(...environmentIds);
    } else {
        // Specific environment selected - show only that environment
        envsToDisplay.push(env);
    }

    return (
        <>
            <div className="panel-content">
                <p>
                    <span className="panel-author-label">Author:</span>
                    <span>{commit.commitMetaData.authorName}</span>
                </p>
                <p>
                    <a href={`/service/${service.name}/CommitDetails/${commit.commitId}?env=${detailsEnv}&process=${process}&metric=${metric}`} target="_blank" rel="noopener noreferrer">
                        View Full Details →
                    </a>
                </p>
            </div>
            <div className="panel-section">
                <h3 className="panel-section-title">Performance Metrics</h3>
                {envsToDisplay.map(targetEnv => {
                    const envName = service.Environments?.find(e => e.id === targetEnv)?.name || `Environment ${targetEnv}`;
                    return (
                        <div key={targetEnv}>
                            {envsToDisplay.length > 1 && (
                                <h4 className="panel-env-title">{envName}</h4>
                            )}
                            <CommitDetailsGraph
                                serviceId={service.id}
                                env={targetEnv}
                                process={process}
                                type={COMMIT}
                                commitDetails={commit}
                                metric={metric}
                                showBottomButtons={false}
                            />
                        </div>
                    );
                })}
                <div className="panel-actions-wrapper">
                    <LegendButton />
                    <VsoLink commitMetadata={commit.commitMetaData} service={service} />
                    {metric !== MetricULS && <ParentButtonsComponent serviceId={service.id} commitDetails={commit} env={detailsEnv} processId={process} metric={metric} />}
                </div>
            </div>
        </>
    );
}
